import { Fragment, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ChevronRight } from "lucide-react";
import { PageContent } from "../../components/PageContent";
import { Card } from "../../components/Card";
import { SectionTitle } from "../../components/SectionTitle";
import { LoadingContainer } from "../../components/LoadingContainer";
import { InfoBox } from "../../components/InfoBox";
import { Input } from "../../components/Input";
import { SelectInput } from "../../components/SelectInput";
import { FormLabel } from "../../components/FormLabel";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../../components/Table";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  useGetInventoryCostingQuery,
  useGetSerialIssuesQuery,
} from "../../query/inventoryCostingQuery";
import { formatMoney } from "../../utils/formatMoney";
import { formatDateTime } from "../../utils/dateUtils";
import { CostLayersPanel } from "./CostLayersPanel";
import { stockBalanceIdForSku } from "@KassaroAB/shared/utils/stockBalance";

const ALL_LOCATIONS = "";

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
`;

const Section = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing.x2}px;
`;

const Muted = styled.p`
  margin: 0;
  color: ${colors.textMuted};
  font-size: ${fontSizes.medium}px;
`;

const Badge = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  padding: 2px ${spacing.x1}px;
  border-radius: ${borderRadius.small}px;
  background: ${colors.successBackground};
  color: ${colors.successText};
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.x2}px;
`;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  padding: ${spacing.x2}px;
  border-radius: ${borderRadius.regular}px;
  background: ${colors.backgroundLight};
`;

const TileLabel = styled.span`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const TileValue = styled.span`
  font-size: ${fontSizes.xlarge}px;
  font-weight: 600;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  align-items: flex-end;
`;

const Filter = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  flex: 1 1 240px;
  max-width: 360px;
`;

const TableBox = styled.div`
  overflow-x: auto;
`;

const ExpandButton = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: ${borderRadius.small}px;
  background: transparent;
  color: ${colors.text};
  cursor: pointer;

  svg {
    transition: transform 0.15s;
    transform: rotate(${({ $open }) => ($open ? 90 : 0)}deg);
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const ExpandedCell = styled.td`
  padding: 0 ${spacing.x2}px ${spacing.x2}px 56px;
  background: ${colors.backgroundLight};
`;

const WarningText = styled.span`
  color: ${colors.warningText};
`;

export const StockView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const {
    data: costing,
    isLoading,
    error,
  } = useGetInventoryCostingQuery(storeId ?? null);
  const { data: serialIssues = [] } = useGetSerialIssuesQuery(
    costing?.settings ? (storeId ?? null) : null,
  );
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState(ALL_LOCATIONS);
  const [openRow, setOpenRow] = useState<string | null>(null);

  const locations = useMemo(
    () =>
      Array.from(
        new Map(
          (costing?.items ?? []).map((item) => [item.poolKey, item.poolName]),
        ),
      ),
    [costing],
  );
  const items = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (costing?.items ?? [])
      .filter(
        (item) =>
          (location === ALL_LOCATIONS || item.poolKey === location) &&
          (!query ||
            `${item.name} ${item.variantName ?? ""}`
              .toLowerCase()
              .includes(query)),
      )
      .sort((a, b) => b.value.amount - a.value.amount);
  }, [costing, search, location]);

  if (isLoading && !costing) {
    return (
      <PageContent title={t("stock.title")}>
        <LoadingContainer message={t("stock.loading")} />
      </PageContent>
    );
  }

  if (!costing?.settings) {
    return (
      <PageContent title={t("stock.title")}>
        {error && <InfoBox variant="error">{t("stock.errorLoad")}</InfoBox>}
        <Section>
          <SectionTitle>{t("stock.notActiveTitle")}</SectionTitle>
          <Muted>{t("stock.notActiveDescription")}</Muted>
          <Link to={`/stores/${storeId}/settings/stock-valuation`}>
            {t("stock.openSettings")}
          </Link>
        </Section>
      </PageContent>
    );
  }

  const provisionalCount = costing.items.filter(
    (item) => item.provisional,
  ).length;

  return (
    <PageContent title={t("stock.title")}>
      <Sections>
        <Section as="section" aria-labelledby="stock-value-title">
          <Header>
            <div>
              <SectionTitle id="stock-value-title">
                {t("stock.valueTitle")}
              </SectionTitle>
              <Muted>
                {t("stock.activeSince", {
                  date: formatDateTime(costing.settings.activatedAt),
                  pool: t(
                    `storeSettings.valuation.poolInline.${costing.settings.pool}`,
                  ),
                })}
              </Muted>
            </div>
            <Badge>{t("storeSettings.valuation.active")}</Badge>
          </Header>
          <Tiles>
            <Tile>
              <TileLabel>{t("stock.totalValue")}</TileLabel>
              <TileValue>{formatMoney(costing.totals.value)}</TileValue>
            </Tile>
            <Tile>
              <TileLabel>{t("stock.inStock")}</TileLabel>
              <TileValue>
                {t("stock.units", { count: costing.totals.quantity })}
              </TileValue>
            </Tile>
            <Tile>
              <TileLabel>{t("stock.provisional")}</TileLabel>
              <TileValue>
                {t("stock.products", { count: provisionalCount })}
              </TileValue>
            </Tile>
          </Tiles>
          <Filters>
            <Filter>
              <FormLabel as="span">{t("stock.search")}</FormLabel>
              <Input
                type="search"
                value={search}
                placeholder={t("stock.searchPlaceholder")}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Filter>
            <Filter>
              <FormLabel as="span">{t("stock.location")}</FormLabel>
              <SelectInput
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                <option value={ALL_LOCATIONS}>{t("stock.allLocations")}</option>
                {locations.map(([poolKey, poolName]) => (
                  <option key={poolKey} value={poolKey}>
                    {poolName}
                  </option>
                ))}
              </SelectInput>
            </Filter>
          </Filters>
          {items.length > 0 ? (
            <TableBox>
              <Table minWidth="640px">
                <TableHeader>
                  <tr>
                    <TableHeaderCell>
                      <VisuallyHidden>{t("stock.layers.show")}</VisuallyHidden>
                    </TableHeaderCell>
                    <TableHeaderCell>{t("stock.product")}</TableHeaderCell>
                    <TableHeaderCell>{t("stock.location")}</TableHeaderCell>
                    <TableHeaderCell align="right">
                      {t("stock.quantity")}
                    </TableHeaderCell>
                    <TableHeaderCell align="right">
                      {t("stock.averageCost")}
                    </TableHeaderCell>
                    <TableHeaderCell align="right">
                      {t("stock.value")}
                    </TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const skuKey = stockBalanceIdForSku(
                      item.productId,
                      item.variantId,
                    );
                    const rowKey = `${item.poolKey}/${skuKey}`;
                    const isOpen = openRow === rowKey;
                    const title = item.variantName
                      ? `${item.name} — ${item.variantName}`
                      : item.name;
                    const toggle = () => setOpenRow(isOpen ? null : rowKey);
                    return (
                      <Fragment key={rowKey}>
                        <TableRow clickable active={isOpen} onClick={toggle}>
                          <TableCell>
                            <ExpandButton
                              type="button"
                              $open={isOpen}
                              aria-expanded={isOpen}
                              aria-label={t("stock.layers.showFor", {
                                name: title,
                              })}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggle();
                              }}
                            >
                              <ChevronRight size={18} aria-hidden="true" />
                            </ExpandButton>
                          </TableCell>
                          <TableCell>{title}</TableCell>
                          <TableCell noWrap>{item.poolName}</TableCell>
                          <TableCell align="right" noWrap>
                            {item.quantity}
                            {item.provisional ? " *" : ""}
                          </TableCell>
                          <TableCell align="right" noWrap>
                            {item.quantity > 0
                              ? formatMoney({
                                  amount: Math.round(
                                    item.value.amount / item.quantity,
                                  ),
                                  currency: item.value.currency,
                                })
                              : "—"}
                          </TableCell>
                          <TableCell align="right" noWrap>
                            {formatMoney(item.value)}
                          </TableCell>
                        </TableRow>
                        {isOpen && storeId && (
                          <tr>
                            <ExpandedCell colSpan={6}>
                              <CostLayersPanel
                                storeId={storeId}
                                poolKey={item.poolKey}
                                skuKey={skuKey}
                              />
                            </ExpandedCell>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableBox>
          ) : (
            <Muted>{t("stock.empty")}</Muted>
          )}
          {provisionalCount > 0 && <Muted>{t("stock.provisionalNote")}</Muted>}
        </Section>
        {serialIssues.length > 0 && (
          <Section as="section" aria-labelledby="serial-issues-title">
            <div>
              <SectionTitle id="serial-issues-title">
                {t("stock.serialIssues")}
              </SectionTitle>
              <Muted>{t("stock.serialIssuesDescription")}</Muted>
            </div>
            <TableBox>
              <Table minWidth="640px">
                <TableHeader>
                  <tr>
                    <TableHeaderCell>{t("stock.date")}</TableHeaderCell>
                    <TableHeaderCell>{t("stock.product")}</TableHeaderCell>
                    <TableHeaderCell>{t("stock.serial")}</TableHeaderCell>
                    <TableHeaderCell>{t("stock.issue")}</TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {serialIssues.map((issue) => (
                    <TableRow
                      key={`${issue.id}-${issue.productId}-${issue.variantId}`}
                    >
                      <TableCell noWrap>
                        {formatDateTime(issue.occurredAt)}
                      </TableCell>
                      <TableCell>{issue.name}</TableCell>
                      <TableCell>{issue.serials.join(", ") || "—"}</TableCell>
                      <TableCell>
                        <WarningText>
                          {t(`stock.issueReason.${issue.reason}`)}
                        </WarningText>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBox>
          </Section>
        )}
      </Sections>
    </PageContent>
  );
};
